import { AppliedCreditsMethod } from 'components/payment/quick-checkout/components/applied-credits-method';
import {
  ErrorLabel,
  ErrorWrapper,
  StyledErrorIcon,
  StyledPaymentBlock,
  StyledPaymentMethod,
  NativeErrorLabel,
  StyledAfterPaymentMethod,
} from 'components/payment/quick-checkout/components/styles';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { PaymentMethodTypes } from '@hqo/react-components-library/dist/payment/payment-method';
import { Order } from 'store/cart/types';
import { SavedPaymentMethod } from 'store/payment/types';
import { resolvePaymentMethodText } from 'utils/resolvePaymentMethodText';
import { PaymentMethodManager } from 'components/payment/quick-checkout/components/payment-methods-selector';
import { AFTER_PAYMENT, INVOICE } from 'shared/consts/payment-method-types';
import { getCartErrorMessage, isCapacityExceededError } from 'utils/getCartErrorMessage';
import { VisitorRegistrationErrorsEnum } from 'shared/consts/errors';
import { useSelector } from 'react-redux';
import { selectPaymentMethods } from 'store/payment/selectors';
import { selectResource } from 'store/resource/selectors';
import { usePriceQuery } from 'data/queries/getPriceQuery.hook';
import { InfoCircle } from '@hqo/react-components-library/dist/icons';

export interface PaymentMethodsSectionProps {
  onEditClick: VoidFunction;
  cart: Order;
  isProcessPaymentFailed: boolean;
  isVisiblePaymentMethodBlock: boolean;
  currentPaymentMethodId: string;
  paymentMethod: SavedPaymentMethod;
  cartError: SerializableError;
  isNativePaymentError: boolean;
  isAllowedVisitorsExist?: boolean;
}

export const PaymentMethodsSection = ({
  onEditClick,
  cart,
  isProcessPaymentFailed,
  isVisiblePaymentMethodBlock,
  currentPaymentMethodId,
  paymentMethod,
  cartError,
  isNativePaymentError,
  isAllowedVisitorsExist = false,
}: PaymentMethodsSectionProps) => {
  const { enableHqoMiniappApplePay } = useFlags();
  const intl = useIntl();
  const paymentMethods = useSelector(selectPaymentMethods);
  const paymentMethodText = resolvePaymentMethodText(paymentMethod, intl);
  const isPaymentErrorVisible = useMemo<boolean>(
    () => isProcessPaymentFailed || isNativePaymentError,
    [isProcessPaymentFailed, isNativePaymentError],
  );
  const resource = useSelector(selectResource);
  const priceQuery = usePriceQuery({
    resourceId: resource?.id,
    timezone: resource?.timezone,
  });
  const creditTotal = priceQuery?.data?.price?.credit_total;
  const availableCredit = priceQuery?.data?.price?.available_credit;
  const availableCreditAmount = availableCredit != null ? Math.ceil(availableCredit) : undefined;
  const creditPrice = creditTotal != null ? Math.ceil(creditTotal) : undefined;

  const hasCreditPrice = useMemo(() => cart?.items?.some(item => item.credit_price), [cart?.items]);

  const hasMultiplePaymentOptions =
    hasCreditPrice ||
    (enableHqoMiniappApplePay &&
      navigator.userAgent.includes('iPhone') &&
      ![INVOICE, AFTER_PAYMENT].includes(paymentMethod?.payment_method_type)) ||
    paymentMethods?.length > 1;

  const errorMessage = (() => {
    if (isProcessPaymentFailed && cartError?.message?.includes('already booked')) {
      return intl.formatMessage({ id: 'booking_unavailable_error' });
    }
    // A duplicate guest profile is not a payment problem, so the generic payment_error below would
    // tell the booker to switch cards for something no card can fix. Keep its specific copy even
    // when several payment options exist. RES-1011.
    //
    // Gated on isProcessPaymentFailed, matching the branch above: isNativePaymentError is local
    // Apple Pay state while cartError comes from the store, so without this a stale cart error
    // would describe a native payment failure.
    if (isProcessPaymentFailed && cartError?.response?.errorCode === VisitorRegistrationErrorsEnum.DUPLICATE_PROFILE) {
      return intl.formatMessage({ id: getCartErrorMessage(cartError) });
    }
    // Too many people for the resource is not a payment problem either: switching payment method
    // cannot fix it, so keep the capacity copy even when several payment options exist. RES-1423.
    if (isProcessPaymentFailed && isCapacityExceededError(cartError)) {
      return intl.formatMessage({ id: getCartErrorMessage(cartError) });
    }
    if (hasMultiplePaymentOptions) {
      return intl.formatMessage({ id: 'payment_error' });
    }
    return intl.formatMessage({ id: getCartErrorMessage(cartError) });
  })();

  return hasMultiplePaymentOptions ? (
    <>
      {isVisiblePaymentMethodBlock && (
        <PaymentMethodManager
          onEditClick={onEditClick}
          creditPrice={creditPrice}
          availableCredit={availableCreditAmount}
          creditCycle={priceQuery?.data?.price?.credit_cycle}
          creditsAssignedUntil={priceQuery?.data?.price?.credits_assigned_until}
        />
      )}
      {isPaymentErrorVisible && (
        <ErrorWrapper>
          <NativeErrorLabel>{errorMessage}</NativeErrorLabel>
        </ErrorWrapper>
      )}
    </>
  ) : (
    <StyledPaymentBlock isAllowedVisitorsExist={isAllowedVisitorsExist}>
      {isVisiblePaymentMethodBlock && (
        <StyledPaymentMethod
          methodText={currentPaymentMethodId && paymentMethodText}
          paymentMethod={paymentMethod?.payment_method_type as PaymentMethodTypes}
          editButtonText={intl.formatMessage({ id: 'edit' })}
          onEditClick={onEditClick}
        />
      )}
      {paymentMethod?.payment_method_type === AFTER_PAYMENT && (
        <StyledAfterPaymentMethod data-testid="after-payment-method">
          <InfoCircle />
          {intl.formatMessage({ id: 'after_payment_info' })}
        </StyledAfterPaymentMethod>
      )}
      <AppliedCreditsMethod cart={cart} />
      {isProcessPaymentFailed && (
        <ErrorWrapper>
          <ErrorLabel>{errorMessage}</ErrorLabel>
          <StyledErrorIcon size="sm" variant="pink" />
        </ErrorWrapper>
      )}
    </StyledPaymentBlock>
  );
};
